<template>
  <div class="god_page_container">
    <h2>Click and plan God dashboard</h2>
    <div>
      <div class="fields">
        <div class="input-33">
          <label>Fecha inicio</label>
          <br/>
          <input
            type="date"
            required
            class="expiration-date"
            v-model="min_date" />
        </div>
        <div class="input-33">
          <label>Fecha fin</label>
          <br/>
          <input
            type="date"
            required
            class="expiration-date"
            v-model="max_date" />
        </div>
        <button @click="getStatistics()" class="input_33 main_button" style="margin-top: 22px;">Actualizar gráficas</button>
      </div>
      <div class="fields">
        <div class="input-100">
          <table style="width: 100%; margin-top: 20px;">
            <tbody>
              <tr>
                <td style="text-align: center;">
                  <strong>Total de compras:</strong> {{ totalNumPurchases.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) }}
                </td>
                <td style="text-align: center;">
                  <strong>Importe total:</strong> {{ totalAmountPaid.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}€
                </td>
                <td style="text-align: center;">
                  <strong>Total GGE:</strong> {{ totalGGE.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}€
                </td>
                <td style="text-align: center;">
                  <strong>Total GGU:</strong> {{ totalGGU.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}€
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="fields">
        <div class="input-100">
          <canvas ref="chartRef" style="margin-top: 10px;" class="chart-canvas"></canvas>
        </div>
      </div>
      <div class="fields">
        <div class="input-33">
          <canvas ref="chartCitiesRef" style="margin-top: 20px;" class="chart-canvas-100"></canvas>
        </div>
        <div class="input-33" style="margin-top: 20px;">
          <select 
            v-model="plansByCityFilter"
            v-if="plans_by_company">
            <option
              v-for="(item, index) in uniqueCities"
              :key="index"
              :value="item.company_city">
              {{ item.company_city }}
            </option>
          </select>

          <canvas ref="chartPlansByCompanyRef" style="margin-top: 10px;" class="chart-canvas-tall"></canvas>
        </div>
        <div class="input-33" style="text-align: center;">
          <img src="@/assets/images/home_logo.png" alt="Click and Plan God Logo" style="margin-top: 200px; max-width: 100%;"/>
        </div>

      </div>
    </div>

    <ErrorPopup :errorMessage="error" />
  </div>

</template>

<script setup>
import { ref, watch, onMounted, computed, nextTick } from 'vue';
import { Chart, registerables } from 'chart.js';
import ErrorPopup from "@/components/common/ErrorPopup.vue";
import { useLoadingStore } from "@/stores/loadingStore";
import { refreshToken } from "@/services/authService";
import { useRouter } from 'vue-router';

const error = ref(null);
const loadingStore = useLoadingStore();
const router = useRouter();

Chart.register(...registerables);

const purchases_by_date = ref([]);
const plans_by_city = ref([]);
const plans_by_company = ref([]);
const formatDate = (date) => date.toISOString().split('T')[0];
const plansByCityFilter = ref('');

const today = new Date();
const lastMonth = new Date();
lastMonth.setDate(today.getDate() - 30);

const max_date = ref(formatDate(today));
const min_date = ref(formatDate(lastMonth));

const chartLabels = computed(() => purchases_by_date.value.map(item => item.day));
const comprasData = computed(() => purchases_by_date.value.map(item => item.numero_compras));
const totalPagadoData = computed(() => purchases_by_date.value.map(item => parseFloat(item.suma_total_pagado) || 0));
const sumaGGEData = computed(() => purchases_by_date.value.map(item => parseFloat(item.suma_GGE) || 0));
const sumaGGUData = computed(() => purchases_by_date.value.map(item => parseFloat(item.suma_GGU) || 0));

const chartCitieslabels = computed(() => plans_by_city.value.map(item => item.city || "Sin ciudad"));
const chartCitesTotals = computed(() => plans_by_city.value.map(item => item.total));

const filteredPlans = computed(() => {
  if (!plansByCityFilter.value) return plans_by_company.value;
  return plans_by_company.value.filter(
    item => item.company_city === plansByCityFilter.value
  );
});

const chartPlansByCompanyLabels = computed(() =>
  filteredPlans.value.map(item => item.company || "Sin empresa")
);

const chartPlansByCompanyTotals = computed(() =>
  filteredPlans.value.map(item => item.num_plans)
);

const uniqueCities = computed(() => {
  const seen = new Set()
  return plans_by_company.value.filter(item => {
    if (!item.company_city || seen.has(item.company_city)) {
      return false
    }
    if (plansByCityFilter.value == '') {
      plansByCityFilter.value = item.company_city;
    }
    seen.add(item.company_city)
    return true
  })
})

const chartRef = ref(null);
const chartCitiesRef = ref(null);
const chartPlansByCompanyRef = ref(null);
const totalNumPurchases = ref(0.0);
const totalAmountPaid = ref(0.0);
const totalGGE = ref(0.0);
const totalGGU = ref(0.0);
let chartInstance = null;
let chartCitiesInstance = null;
let chartPlansByCompanyInstance = null;

onMounted(() => {
  chartInstance = new Chart(chartRef.value, {
    type: 'bar',
    data: {
      labels: chartLabels.value,
      datasets: [
        { type: 'line', label: 'Número de compras', data: comprasData.value, borderWidth: 2, tension: 0.2, borderColor: '#FF0000', backgroundColor: '#FF000077'},
        { type: 'bar', label: 'Total pagado (€)', data: totalPagadoData.value, borderWidth: 2, tension: 0.2, borderColor: '#20CDA8', backgroundColor: '#20CDA877'},
        { type: 'line', label: 'GGE', data: sumaGGEData.value, borderWidth: 2, tension: 0.2, borderColor: '#003F7D', backgroundColor: '#003F7D77'},
        { type: 'line', label: 'GGU', data: sumaGGUData.value, borderWidth: 2, tension: 0.2, borderColor: '#FF8C42', backgroundColor: '#FF8C4277'}
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false
      },
      scales: {
        y: {
          beginAtZero: true
        }
      }
    }
  });

  chartCitiesInstance = new Chart(chartCitiesRef.value, {
    type: 'bar',
    data: {
      labels: chartCitieslabels.value,
      datasets: [
        {
          label: 'Número de planes por ciudad',
          data: chartCitesTotals.value,
          borderWidth: 2,
          borderColor: '#20CDA8',
          backgroundColor: '#20CDA877'
        }
      ]
    },
    options: {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          beginAtZero: true
        },
        y: {
          ticks: {
            autoSkip: false,
            maxRotation: 45,
            minRotation: 30,
            callback: function(value) {
              const label = this.getLabelForValue(value)
              const maxLength = 20
              return label.length > maxLength
                ? label.substring(0, maxLength) + '…'
                : label
            }
          }
        }
      }
    }
  });

  chartPlansByCompanyInstance = new Chart(chartPlansByCompanyRef.value, {
    type: 'bar',
    data: {
      labels: chartPlansByCompanyLabels.value,
      datasets: [
        {
          label: 'Número de planes por empresa',
          data: chartPlansByCompanyTotals.value,
          borderWidth: 2,
          borderColor: '#20CDA8',
          backgroundColor: '#20CDA877'
        }
      ]
    },
    options: {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          beginAtZero: true
        },
        y: {
          ticks: {
            autoSkip: false,
            maxRotation: 45,
            minRotation: 30,
            callback: function(value) {
              const label = this.getLabelForValue(value)
              const maxLength = 20
              return label.length > maxLength
                ? label.substring(0, maxLength) + '…'
                : label
            }
          }
        }
      }
    }
  });
});

watch(purchases_by_date, () => {
  if (chartInstance) {
    chartInstance.data.labels = chartLabels.value;
    chartInstance.data.datasets[0].data = comprasData.value;
    chartInstance.data.datasets[1].data = totalPagadoData.value;
    chartInstance.data.datasets[2].data = sumaGGEData.value;
    chartInstance.data.datasets[3].data = sumaGGUData.value;
    chartInstance.update();
  }

  totalNumPurchases.value = purchases_by_date.value.reduce((sum, item) => sum + item.numero_compras, 0);
  totalAmountPaid.value = purchases_by_date.value.reduce((sum, item) => sum + (parseFloat(item.suma_total_pagado) || 0), 0);
  totalGGE.value = purchases_by_date.value.reduce((sum, item) => sum + (parseFloat(item.suma_GGE) || 0), 0);
  totalGGU.value = purchases_by_date.value.reduce((sum, item) => sum + (parseFloat(item.suma_GGU) || 0), 0);
});

watch(plans_by_city, () => {
  if (chartCitiesInstance) {
    chartCitiesInstance.data.labels = chartCitieslabels.value;
    chartCitiesInstance.data.datasets[0].data = chartCitesTotals.value;
    chartCitiesInstance.update();
  }
});

watch(chartPlansByCompanyTotals, () => {
  if (chartPlansByCompanyInstance) {
    chartPlansByCompanyInstance.data.labels = chartPlansByCompanyLabels.value;
    chartPlansByCompanyInstance.data.datasets[0].data = chartPlansByCompanyTotals.value;
    chartPlansByCompanyInstance.update();
  }
});

const getStatistics = async () => {
  loadingStore.setLoading(true);
  error.value = null;

  try {
    let response = await callAPIToGetStatistics();

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await callAPIToGetStatistics();
      } else {
        loadingStore.setLoading(false);
        localStorage.removeItem('user_id');
        localStorage.removeItem('token');
        router.push('/');
        return;
      }
    }

    if (!response.ok) {
      showError("Se ha producido un error obteniendo las estadísticas.");
      return;
    }

    const data = await response.json();

    if (data.errors) {
      showError(data.message);
      purchases_by_date.value = [];
      plans_by_city.value = [];
      plans_by_company.value = [];
    } else {
      purchases_by_date.value = data.stats.purchases_by_date;
      plans_by_city.value = data.stats.plans_by_city;
      plans_by_company.value = data.stats.plans_by_company;
    }
  } catch (exception) {
    showError(exception.message);
  } finally {
    loadingStore.setLoading(false);
  }
}

async function callAPIToGetStatistics() {
  return await fetch(process.env.VUE_APP_API_URL+"api/god/stats/get?user_id="+localStorage.getItem('user_id')+"&min_date="+min_date.value+"&max_date="+max_date.value,
      {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "vgod": process.env.VUE_APP_API_VGOD,
        "cp-token": process.env.VUE_APP_API_CP_TOKEN,
        "Authorization": "Bearer " + localStorage.getItem('token')
      },
    });
}

const showError = async (message) => {
  error.value = null;
  await nextTick(); // Espera a que Vue reaccione al cambio
  error.value = message;
};

getStatistics();

</script>

<style scoped>

  .fields {
    display: flex;
    margin-top: 20px;
  }

  .input-33 {
    flex: 0 0 32%;
    margin-right: 1%;
  }

  .input-33:last-child {
    flex: 0 0 33%;
    margin-right: 0%;
  }

  .input-33 input {
    width: 100%;
    box-sizing: border-box;
  }

  .input-33 select {
    width: 100%;
    box-sizing: border-box;
  }

  .input-50 {
    flex: 0 0 49%;
    margin-right: 1%;
  }

  .input-50:last-child {
    flex: 0 0 50%;
    margin-right: 0%;
  }

  .input-66 {
    flex: 0 0 65%;
    margin-right: 1%;
  }

  .input-66:last-child {
    flex: 0 0 66%;
    margin-right: 0%;
  }

  .input-100 {
    flex: 0 0 100%;
    margin-right: 0%;
  }

  .chart-canvas {
    width: 100%;
    height: 600px;
    max-height: 600px;
  }

  .chart-canvas-tall {
    width: 100%;
    height: 800px;
    max-height: 800px;
  }

  .chart-canvas-100 {
    width: 100%;
    height: 100%;
  }
</style>
